// /vouchers — Voucher Management (PAGES.md §3.33)

import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Input,
  Select,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import {
  useVoucherList,
  VoucherStatusPill,
  VoucherCodeChip,
  formatVoucherValue,
  daysUntil,
  formatVoucherDay,
  printVoucher,
} from '@/features/vouchers';
import type { Voucher, VoucherStatus, VoucherType } from '@/features/vouchers';
import { downloadCsv } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useStoreTimezone } from '@/lib/store-clock';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRED', label: 'Expired' },
  { value: 'EXHAUSTED', label: 'Exhausted' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const buildTypeOptions = (symbol: string) => [
  { value: '', label: 'All Types' },
  { value: 'PERCENTAGE', label: 'Percentage' },
  { value: 'FLAT', label: `Flat ${symbol} off` },
];

type Row = Voucher;

function UtilisationBar({ used, max }: { used: number; max: number | null }) {
  if (max == null) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {used.toLocaleString('en-IN')}
        </span>
        <span className="text-xs text-neutral-400">/ ∞</span>
      </div>
    );
  }
  const pct = Math.min(100, Math.round((used / Math.max(max, 1)) * 100));
  const bar = pct < 60 ? 'from-success-500 to-teal-500' : pct < 85 ? 'from-warning-500 to-orange-500' : 'from-danger-500 to-rose-500';
  return (
    <div className="min-w-[8rem]">
      <div className="flex items-center justify-between text-xs tabular-nums mb-1">
        <span className="font-semibold text-neutral-700 dark:text-neutral-200">{used.toLocaleString('en-IN')} / {max.toLocaleString('en-IN')}</span>
        <span className="text-neutral-500 dark:text-neutral-400">{pct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-700/60">
        <div className={`h-full rounded-full bg-gradient-to-r ${bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function ExpiryCell({ expiresAt, timeZone }: { expiresAt: string | null; timeZone: string }) {
  if (!expiresAt) return <span className="text-xs text-neutral-400">No expiry</span>;
  const days = daysUntil(expiresAt);
  const expired = days != null && days < 0;
  const urgent = !expired && days != null && days <= 7;
  return (
    <div>
      <p className="text-sm text-neutral-700 dark:text-neutral-200 tabular-nums">{formatVoucherDay(expiresAt, timeZone, true)}</p>
      {expired ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 dark:bg-neutral-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          Expired
        </span>
      ) : urgent ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-warning-100 dark:bg-warning-900/40 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning-700 dark:text-warning-300">
          {days === 0 ? 'Today' : `${days}d left`}
        </span>
      ) : null}
    </div>
  );
}

function useColumns(): ColumnDef<Row>[] {
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const timeZone = useStoreTimezone();
  const { toast } = useToast();

  const handlePrint = useCallback(
    async (voucher: Row) => {
      const result = await printVoucher(voucher, currency, timeZone);
      if (!result.ok) {
        toast(
          result.reason === 'popup-blocked'
            ? { variant: 'error', title: 'Pop-up blocked', description: 'Allow pop-ups for this site to print the voucher.' }
            : { variant: 'error', title: 'Could not prepare voucher', description: 'Please try again.' },
        );
      }
    },
    [currency, timeZone, toast],
  );

  return useMemo<ColumnDef<Row>[]>(() => [
    {
      key: 'code',
      header: 'Code',
      render: (_v, row) => (
        <button type="button" onClick={() => navigate(`/vouchers/${row.id}`)} className="group">
          <VoucherCodeChip code={row.code} copyable />
        </button>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (_v, row) => {
        const isPercent = row.type === 'PERCENTAGE';
        return (
          <span className={`inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${isPercent ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-200' : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-200'}`}>
            {row.type}
          </span>
        );
      },
    },
    {
      key: 'value',
      header: 'Value',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tabular-nums">{formatVoucherValue(row, currency)}</span>
      ),
    },
    {
      key: 'used_count',
      header: 'Used / Max',
      render: (_v, row) => <UtilisationBar used={row.used_count} max={row.max_uses} />,
    },
    {
      key: 'expires_at',
      header: 'Expires',
      render: (_v, row) => <ExpiryCell expiresAt={row.expires_at} timeZone={timeZone} />,
    },
    {
      key: 'min_order_value',
      header: 'Min Order',
      numeric: true,
      render: (_v, row) =>
        row.min_order_value > 0 ? (
          <span className="text-sm text-neutral-700 dark:text-neutral-300 tabular-nums">{formatPaise(row.min_order_value, currency)}</span>
        ) : (
          <span className="text-xs text-neutral-400">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => <VoucherStatusPill status={row.status} />,
    },
    {
      key: 'id',
      header: '',
      render: (_v, row) => (
        <div className="flex items-center justify-end gap-3 whitespace-nowrap">
          <button
            type="button"
            onClick={() => void handlePrint(row)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 dark:text-violet-300 hover:text-violet-700 dark:hover:text-violet-200 transition-colors"
            aria-label={`Print voucher ${row.code}`}
            title="Print voucher"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 9 6 2 18 2 18 9" />
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
              <rect x="6" y="14" width="12" height="8" />
            </svg>
            Print
          </button>
          <button
            type="button"
            onClick={() => navigate(`/vouchers/${row.id}`)}
            className="text-xs font-semibold text-brand-600 dark:text-brand-300 hover:text-brand-700 dark:hover:text-brand-200 transition-colors"
          >
            View →
          </button>
        </div>
      ),
    },
  ], [navigate, currency, timeZone, handlePrint]);
}

function VoucherListContent() {
  const navigate = useNavigate();
  const columns = useColumns();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const typeOptions = useMemo(() => buildTypeOptions(symbol), [symbol]);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement | null>(null);

  const page = Number(searchParams.get('page') ?? '1');
  const search = searchParams.get('search') ?? '';
  const status = (searchParams.get('status') ?? '') as VoucherStatus | '';
  const type = (searchParams.get('type') ?? '') as VoucherType | '';

  const { data, isLoading, isFetching, error } = useVoucherList({ page, search, status, type });

  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);
  const totalCount = data?.meta.total ?? 0;
  const activeCount = useMemo(() => rows.filter((r) => r.status === 'ACTIVE').length, [rows]);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  // Keyboard shortcuts: / focus search, n → new voucher
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'n') {
        e.preventDefault();
        navigate('/vouchers/new');
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate]);

  const handleExport = useCallback(() => {
    if (rows.length === 0) {
      toast({ variant: 'warning', title: 'Nothing to export', description: 'The current view has no vouchers.' });
      return;
    }
    downloadCsv<Row>(
      `vouchers-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        { header: 'Code', get: (r) => r.code },
        { header: 'Type', get: (r) => r.type },
        { header: 'Value', get: (r) => (r.type === 'PERCENTAGE' ? `${r.value}%` : (r.value / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency))) },
        { header: `Min Order (${symbol})`, get: (r) => (r.min_order_value / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency)) },
        { header: 'Used', get: (r) => r.used_count },
        { header: 'Max Uses', get: (r) => r.max_uses ?? '' },
        { header: 'Starts', get: (r) => r.starts_at ?? '' },
        { header: 'Expires', get: (r) => r.expires_at ?? '' },
        { header: 'Status', get: (r) => r.status },
      ],
      rows,
    );
  }, [rows, toast]);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-600 via-indigo-600 to-violet-600 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(99,102,241,0.6)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Vouchers & Coupons
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Discount Codes</h1>
            <p className="mt-1.5 text-sm text-white/85 max-w-xl">
              Create, track and manage discount codes across every channel.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {totalCount.toLocaleString('en-IN')} total
              </span>
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums">
                {activeCount.toLocaleString('en-IN')} active on this page
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={handleExport} className="bg-white/15 hover:bg-white/25 text-white border-white/30 backdrop-blur-sm">
              Export CSV
            </Button>
            <Button variant="primary" onClick={() => navigate('/vouchers/new')} className="!bg-white !text-indigo-700 hover:!bg-white/90 !border-transparent">
              + Create Voucher
            </Button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="rounded-xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-4 sm:p-5 ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 mb-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex-1 min-w-0">
            <Input
              id="voucher-search"
              variant="search"
              placeholder="Search by code (press / to focus)"
              value={search}
              onChange={(v) => setParam('search', v)}
              aria-label="Search vouchers"
            />
          </div>
          <div className="w-full lg:w-44">
            <Select options={STATUS_OPTIONS} value={status} onChange={(v) => setParam('status', typeof v === 'string' ? v : '')} />
          </div>
          <div className="w-full lg:w-44">
            <Select options={typeOptions} value={type} onChange={(v) => setParam('type', typeof v === 'string' ? v : '')} />
          </div>
        </div>
      </div>

      {/* Data */}
      {error ? (
        <div className="rounded-xl bg-danger-50 dark:bg-danger-900/20 shadow-md p-4 text-sm text-danger-700 dark:text-danger-300">
          Failed to load vouchers. Please refresh.
        </div>
      ) : isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={64} className="rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No vouchers yet"
            description="Create your first discount code to run promotions across every channel."
            action={{ label: '+ Create Voucher', onClick: () => navigate('/vouchers/new') }}
            icon={
              <svg className="h-20 w-20 text-violet-300 dark:text-violet-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
              </svg>
            }
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up">
          <DataTable<Row>
            columns={columns}
            data={rows}
            loading={isFetching}
            totalCount={data?.meta.total}
            currentPage={page}
            pageSize={20}
            onPageChange={(p) => setParam('page', String(p))}
            stickyHeader
          />
        </section>
      )}
    </div>
  );
}

export function VoucherListPage() {
  return (
    <PageContainer title="Vouchers" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Vouchers' }]}>
      <ErrorBoundary resetKey="vouchers-list">
        <VoucherListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
